import { useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  Typography,
  Button,
  FormHelperText,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

export interface FilterOrdersFormData {
  dateFrom: string;
  dateTo: string;
}

interface FilterOrdersProps {
  searchFilters?: FilterOrdersFormData | null;
  onCancel?: () => void;
  onFilter?: (data: FilterOrdersFormData) => void;
}

const FilterOrders = ({
  searchFilters,
  onCancel,
  onFilter,
}: FilterOrdersProps) => {
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FilterOrdersFormData>({
    defaultValues: {
      dateFrom: "",
      dateTo: "",
    },
  });

  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");

  useEffect(() => {
    if (searchFilters) {
      reset({
        dateFrom: searchFilters.dateFrom || "",
        dateTo: searchFilters.dateTo || "",
      });
    }
  }, [searchFilters, reset]);

  const onSubmitHandler = (data: FilterOrdersFormData) => {
    onFilter?.(data);
  };

  const handleCancel = () => {
    reset({ dateFrom: "", dateTo: "" });
    onCancel?.();
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 0.8, minWidth: "180px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Fecha Desde
          </Typography>
          <Controller
            control={control}
            name="dateFrom"
            rules={{
              validate: {
                required: (value) => {
                  if (dateTo && !value) {
                    return "Fecha Desde es requerida si Fecha Hasta está llena";
                  }
                  return true;
                },
                lessThanDateTo: (value) => {
                  if (value && dateTo && value > dateTo) {
                    return "Fecha Desde debe ser menor que Fecha Hasta";
                  }
                  return true;
                },
              },
            }}
            render={({ field, fieldState }) => (
              <FormControl fullWidth error={!!errors.dateFrom}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="es"
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue: Dayjs | null) => {
                      field.onChange(
                        newValue ? newValue.format("YYYY-MM-DD") : "",
                      );
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                    }}
                  />
                </LocalizationProvider>
                {errors.dateFrom && (
                  <FormHelperText>{errors.dateFrom.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>

        <Box sx={{ flex: 0.8, minWidth: "180px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Fecha Hasta
          </Typography>
          <Controller
            control={control}
            name="dateTo"
            rules={{
              validate: {
                required: (value) => {
                  if (dateFrom && !value) {
                    return "Fecha Hasta es requerida si Fecha Desde está llena";
                  }
                  return true;
                },
                greaterThanDateFrom: (value) => {
                  if (value && dateFrom && value < dateFrom) {
                    return "Fecha Hasta debe ser mayor que Fecha Desde";
                  }
                  return true;
                },
              },
            }}
            render={({ field, fieldState }) => (
              <FormControl fullWidth error={!!errors.dateTo}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="es"
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue: Dayjs | null) => {
                      field.onChange(
                        newValue ? newValue.format("YYYY-MM-DD") : "",
                      );
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                    }}
                  />
                </LocalizationProvider>
                {errors.dateTo && (
                  <FormHelperText>{errors.dateTo.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button variant="outlined" color="primary" onClick={handleCancel}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" color="primary">
          Filtrar
        </Button>
      </Box>
    </form>
  );
};

export default FilterOrders;
